import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { Typography } from 'components';
import { motion } from 'framer-motion';
import { AttachmentIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { FC, HTMLAttributes, useMemo, useRef, useState } from 'react';
import { cn } from 'utils/cn';
import FormAddUrl from './FormAddUrl';

export const ALLOWED_TYPES_CONTEXT_LIBRARY = [
  'doc',
  'docx',
  'html',
  'json',
  'md',
  'pdf',
  'pptx',
  'txt'
];

interface IAttach extends HTMLAttributes<HTMLInputElement> {
  isPending: boolean;
  filename?: string;
  onChangeAttachUrl: (value: string) => void;
}

const Attach: FC<IAttach> = ({ onChange, onChangeAttachUrl, filename }) => {
  const t = useTranslations();
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const top = ref?.current && Math.ceil(ref.current.getBoundingClientRect().top);

  const acceptableFiles = useMemo(
    () =>
      ALLOWED_TYPES_CONTEXT_LIBRARY.reduce(
        (acc, value, index) =>
          acc + `.${value}${index !== ALLOWED_TYPES_CONTEXT_LIBRARY.length - 1 ? ', ' : ''}`,
        ''
      ),
    []
  );

  const handleClosePopover = () => setOpen(false);

  const handleOpenPopover = () => setOpen(true);

  return (
    <div
      className="relative flex w-12 flex-col items-end justify-end !outline-none focus:!outline-none"
      ref={ref}
    >
      <input
        className="hidden"
        key={filename ? filename : 'file-empty'} // Force a re-render by changing the key
        type="file"
        id="file"
        name="file"
        onChange={onChange}
        accept={acceptableFiles}
      />
      <Menu>
        {({ close }) => (
          <>
            <MenuButton as="div" className="!outline-none">
              <motion.button
                className={cn(
                  'flex flex-1 flex-col justify-center',
                  'focus:!c ml-2 items-center pb-2 pt-2 !outline-none transition-all'
                )}
                whileTap={{ scale: 0.85 }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 25,
                  restDelta: 10,
                  mass: 1
                }}
              >
                <span
                  className={cn(
                    'group !m-0 !h-10 !w-10 hover:!bg-primary-blue-900',
                    'flex cursor-pointer items-center justify-center rounded-full active:!bg-transparent',
                    'hover:!scale-[1.01]'
                  )}
                >
                  <AttachmentIcon className="group-hover:!text-primary-blue-100" />
                </span>
              </motion.button>
            </MenuButton>
            <Transition
              enter="transition ease-out duration-75"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <MenuItems
                anchor="top start"
                className={cn(
                  'w-36 origin-top-right !bg-transparent p-1 text-sm/6 text-red-500 shadow-none',
                  '!outline-none [--anchor-gap:var(--spacing-1)] focus:!outline-none',
                  'transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0',
                  top ? `!top-[${top - 280}px]` : ''
                )}
                style={{
                  top: 860
                }}
              >
                <div
                  className={cn(
                    'ml-1 rounded-md bg-white p-1 shadow-md',
                    filename ? 'mb-1' : 'mb-2'
                  )}
                >
                  <MenuItem>
                    <label
                      htmlFor="file"
                      className="flex w-full cursor-pointer items-center gap-1 rounded-3xl p-1.5 data-[focus]:bg-primary-blue-900"
                    >
                      <Typography variant="medium5" className="!mt-0.5 text-tertiary-100 ">
                        {t('buttons.attach_file')}
                      </Typography>
                    </label>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={handleOpenPopover}
                      className="flex w-full items-center gap-1 rounded-3xl p-1.5 data-[focus]:bg-primary-blue-900"
                    >
                      <Typography variant="medium5" className="!mt-0.5 text-tertiary-100 ">
                        {t('buttons.attach_url')}
                      </Typography>
                    </button>
                  </MenuItem>
                </div>
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>
      <div
        className={cn(
          'relative transition duration-[300] ease-in-out',
          open ? 'opacity-1' : 'pointer-events-none opacity-0'
        )}
      >
        <div
          className={cn(
            'absolute',
            'ml-[8px] w-72 rounded-md bg-white text-sm/6 shadow-md',
            'translate-x-[-56px] translate-y-[-260px]'
          )}
        >
          <FormAddUrl onClose={handleClosePopover} onChangeAttachUrl={onChangeAttachUrl} />
        </div>
      </div>
    </div>
  );
};

export default Attach;
